import React, { useState } from 'react';

/**
 * Part-to-whole donut, hand-rolled SVG — no chart library.
 *
 * Colors are the validated categorical slots (blue / orange / aqua / violet),
 * assigned in fixed order per series key so a source keeps its color even when
 * another one drops to zero. Segments carry a 2px surface gap, every slice is
 * hoverable, and the caller pairs this with an exact-value table — which is
 * also the required relief for the aqua slot's sub-3:1 contrast on white.
 */

// Fixed slot order — never cycled, never reassigned by rank.
export const SERIES_COLORS = {
    subscription: '#2a78d6',
    coffee: '#eb6834',
    star: '#1baf7a',
    campaign: '#4a3aa7',
};

const SURFACE = '#ffffff';

const polar = (cx, cy, radius, angle) => {
    const rad = ((angle - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
};

function ringSlice(cx, cy, outer, inner, startAngle, endAngle) {
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    const p1 = polar(cx, cy, outer, startAngle);
    const p2 = polar(cx, cy, outer, endAngle);
    const p3 = polar(cx, cy, inner, endAngle);
    const p4 = polar(cx, cy, inner, startAngle);
    return [
        `M ${p1.x} ${p1.y}`,
        `A ${outer} ${outer} 0 ${largeArc} 1 ${p2.x} ${p2.y}`,
        `L ${p3.x} ${p3.y}`,
        `A ${inner} ${inner} 0 ${largeArc} 0 ${p4.x} ${p4.y}`,
        'Z',
    ].join(' ');
}

/**
 * @param {Array<{key: string, label: string, value: number}>} data
 * @param {string} centerLabel  small caption under the hero number
 * @param {(n: number) => string} format
 */
function DonutChart({ data, centerLabel, format = (n) => n.toFixed(2), size = 200 }) {
    const [active, setActive] = useState(null);

    const slices = (data || []).filter(item => item.value > 0);
    const total = slices.reduce((sum, item) => sum + item.value, 0);

    const cx = size / 2;
    const cy = size / 2;
    const outer = size / 2 - 4;
    const inner = outer * 0.62;

    // Empty state: a plain ring, so the layout does not jump.
    if (!total) {
        return (
            <div className="flex flex-col items-center">
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={centerLabel}>
                    <circle cx={cx} cy={cy} r={(outer + inner) / 2} fill="none"
                        stroke="#e2e8f0" strokeWidth={outer - inner} />
                    <text x={cx} y={cy + 2} textAnchor="middle" className="fill-slate-300"
                        style={{ fontSize: 22, fontWeight: 800 }}>—</text>
                </svg>
            </div>
        );
    }

    // A 2px surface gap between fills, expressed as an angle at the outer edge.
    const gapDegrees = slices.length > 1 ? (2 / (2 * Math.PI * outer)) * 360 : 0;

    let cursor = 0;
    const arcs = slices.map(item => {
        const sweep = (item.value / total) * 360;
        const start = cursor;
        const end = cursor + sweep;
        cursor = end;
        return {
            ...item,
            start: start + gapDegrees / 2,
            end: Math.max(start + gapDegrees / 2 + 0.4, end - gapDegrees / 2),
            share: item.value / total,
        };
    });

    const focused = arcs.find(arc => arc.key === active);

    return (
        <div className="relative flex flex-col items-center">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
                role="img"
                aria-label={`${centerLabel}: ${arcs.map(a => `${a.label} ${format(a.value)}`).join(', ')}`}>
                {arcs.map(arc => (
                    <path
                        key={arc.key}
                        d={ringSlice(cx, cy, outer, inner, arc.start, arc.end)}
                        fill={SERIES_COLORS[arc.key] || '#94a3b8'}
                        stroke={SURFACE}
                        strokeWidth={active === arc.key ? 0 : 1}
                        opacity={active && active !== arc.key ? 0.35 : 1}
                        style={{ transition: 'opacity 150ms ease', cursor: 'pointer' }}
                        onMouseEnter={() => setActive(arc.key)}
                        onMouseLeave={() => setActive(null)}
                        onFocus={() => setActive(arc.key)}
                        onBlur={() => setActive(null)}
                        tabIndex={0}
                    >
                        <title>{`${arc.label}: ${format(arc.value)} (${Math.round(arc.share * 100)}%)`}</title>
                    </path>
                ))}

                {/* Hero figure — the hovered slice, or the total at rest */}
                <text x={cx} y={cy - 4} textAnchor="middle" className="fill-slate-900"
                    style={{ fontSize: focused ? 20 : 22, fontWeight: 800 }}>
                    {format(focused ? focused.value : total)}
                </text>
                <text x={cx} y={cy + 14} textAnchor="middle" className="fill-slate-400"
                    style={{ fontSize: 10, fontWeight: 600 }}>
                    {focused ? `${focused.label} · ${Math.round(focused.share * 100)}%` : centerLabel}
                </text>
            </svg>
        </div>
    );
}

export default DonutChart;
